// ============================================================================
// BUILD EHR NOTE (NO PHI) — Plain-text note safe for pasting into EHR systems
//
// INVARIANT: This function NEVER touches PHI sources. No patient name, DOB,
// MRN, phone, email, or address is included. Calling this function does NOT
// trigger any Cloud Function calls or phi_access_logs entries.
//
// All inputs are non-PHI only:
//   - receiptToken     — stable RG-XXXXXXX lookup token (write-once)
//   - referral core    — outcome, packId, specialtyId, answers
//   - pack schema      — field labels, red flag labels, escalation options
//   - engine decision  — completeness, missingFields, triggeredRedFlags
//   - attachment files — type + EHR-friendly status (Attached/Missing/Pending)
//   - workflowStatus   — current workflow state for "Next Step" guidance
//   - reworkItemTypes  — missing doc categories from triage requests (if any)
//
// Output: a plain-text string suitable for clipboard / EHR notes.
// ============================================================================

const pad  = (s, width = 60) => String(s).padEnd(width, ' ');
const line = (char = '─', len = 64) => char.repeat(len);
const section = (title) => `\n${line()}\n${title}\n${line()}`;

const OUTCOME_LABELS = {
  routine:  'ROUTINE  — Referral approved',
  urgent:   'URGENT   — Immediate pathway required (red flag triggered)',
  not_yet:  'NOT YET  — Additional workup required before referral',
};

const ATT_TYPE_LABELS = {
  LABS:    'Lab Results',
  IMAGING: 'Imaging Report',
  PDF:     'PDF Document',
  PHOTO:   'Photo / Scan',
  OTHER:   'Other',
};

const REWORK_ITEM_LABELS = {
  LABS:            'Lab results',
  IMAGING_REPORT:  'Imaging report',
  OTHER_DOC:       'Other documentation',
};

// Map raw attachment status to EHR-friendly label (never expose "FAILED")
const ehrAttStatus = (status) => {
  if (status === 'ready') return 'Attached';
  if (status === 'failed') return 'Missing (re-upload required)';
  return 'Pending upload'; // staged | uploading | anything else
};

// Workflow/Next Step mapping — indexed by WORKFLOW_STATUS values
const NEXT_STEP_LABELS = {
  NEW:               'Awaiting triage review by intake team',
  NEEDS_INFO:        'Additional documentation required — please respond to the rework request',
  READY_TO_SCHEDULE: 'Ready for scheduling — awaiting appointment booking by specialist office',
  SCHEDULED:         'Appointment scheduled — no further action required',
  SEEN:              'Seen — post-visit documentation expected',
  CLOSED_LOOP:       'Closed loop — outcome received and recorded',
};

/**
 * Builds a clinically actionable plain-text EHR note containing zero PHI.
 *
 * @param {object} params
 * @param {string}   params.receiptToken        — Stable RG-XXXXXXX lookup token
 * @param {string}   params.runId               — Internal run ID (shown as secondary reference)
 * @param {string}   params.referralId          — Internal Firestore referral ID
 * @param {string}   params.tenantId            — (Not shown — retained for caller auditing only)
 * @param {string}   params.specialtyName       — Destination specialty display name
 * @param {string}   params.packName            — Clinical pack name
 * @param {string}   params.packVersion         — Pack version string
 * @param {string}   params.outcome             — 'routine'|'urgent'|'not_yet'
 * @param {number}   params.completenessScore   — 0-100
 * @param {string}   params.visitType           — e.g. 'in-person', 'video'
 * @param {string}   params.reasonForEscalation — Human-readable reason
 * @param {object[]} params.missingFields        — [{id, label}] engine-computed missing items
 * @param {object[]} params.triggeredRedFlags    — [{id, label}]
 * @param {object[]} params.requiredFields       — [{id, label, required}] from pack (all required fields)
 * @param {object}   params.clinicalAnswers      — { [fieldId]: value }
 * @param {object[]} params.attachmentFiles      — [{type, status}] (no filenames)
 * @param {string[]} params.reworkItemTypes      — Item type codes from rework requests, if any
 *                                                 (e.g. ['LABS', 'IMAGING_REPORT'])
 * @param {object|null} params.overrideInfo      — { reason, note } or null
 * @param {string}   params.submittedAt          — ISO timestamp
 * @param {string}   params.referrerDisplayName  — GP display name (non-PHI provider identity)
 * @param {string|null} params.workflowStatus    — Current workflow status string or null
 * @returns {string} Plain-text EHR note, zero PHI
 */
export const buildEhrNoteNoPhi = ({
  receiptToken,
  runId,
  referralId,
  tenantId,       // retained for caller, not printed in note
  specialtyName,
  packName,
  packVersion,
  outcome,
  completenessScore,
  visitType,
  reasonForEscalation,
  missingFields = [],
  triggeredRedFlags = [],
  requiredFields = [],
  clinicalAnswers = {},
  attachmentFiles = [],
  reworkItemTypes = [],
  overrideInfo = null,
  submittedAt,
  referrerDisplayName,
  workflowStatus = null,
}) => {
  const wasOverridden = Boolean(overrideInfo?.reason);
  const displayOutcome = wasOverridden ? 'routine' : outcome;
  const outcomeLabel = OUTCOME_LABELS[displayOutcome] || displayOutcome?.toUpperCase();
  const ts = submittedAt
    ? new Date(submittedAt).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })
    : new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' });

  const lines = [];

  // ── Header ──────────────────────────────────────────────────────────────────
  lines.push('REFERRAL SUMMARY (NO PHI) — ReferralGate');
  lines.push(line('═'));
  lines.push(`Receipt Token     : ${receiptToken || '—'}`);
  lines.push(`Submitted         : ${ts}`);
  if (referrerDisplayName) lines.push(`Referring Provider: ${referrerDisplayName}`);
  lines.push('');

  // ── Pathway ─────────────────────────────────────────────────────────────────
  lines.push(section('PATHWAY'));
  lines.push(`Specialty         : ${specialtyName || '—'}`);
  lines.push(`Pack              : ${packName || '—'} v${packVersion || '?'}`);
  lines.push(`Outcome           : ${outcomeLabel}`);
  if (wasOverridden) {
    lines.push(`Override          : YES — ${overrideInfo.reason}`);
    if (overrideInfo.note) lines.push(`Override Note     : ${overrideInfo.note}`);
  }
  lines.push(`Completeness      : ${completenessScore ?? '—'}%`);
  lines.push('');

  // ── Referral Summary ────────────────────────────────────────────────────────
  lines.push(section('REFERRAL SUMMARY'));
  lines.push(`Visit Type        : ${visitType || '—'}`);
  lines.push(`Reason            : ${reasonForEscalation || '—'}`);
  lines.push('');

  // ── Red Flags ───────────────────────────────────────────────────────────────
  if (triggeredRedFlags.length > 0) {
    lines.push(section('RED FLAGS TRIGGERED'));
    for (const rf of triggeredRedFlags) {
      lines.push(`  ⚠  ${rf.label}`);
    }
    lines.push('');
  }

  // ── Criteria Snapshot ───────────────────────────────────────────────────────
  lines.push(section('CRITERIA SNAPSHOT'));
  if (requiredFields.length > 0) {
    lines.push('Required Items:');
    for (const field of requiredFields) {
      const answered =
        clinicalAnswers[field.id] !== undefined &&
        clinicalAnswers[field.id] !== null &&
        clinicalAnswers[field.id] !== '';
      const status = answered ? '✓ Provided' : '✗ Missing ';
      lines.push(`  ${status.padEnd(14)} ${field.label}`);
    }
  } else {
    lines.push('  (No required criteria for this pack)');
  }
  lines.push('');

  // ── Missing Items ───────────────────────────────────────────────────────────
  // Prefer rework request item types (from triage review) when available;
  // fall back to engine-computed missingFields for immediate post-submit note.
  lines.push(section('MISSING / OUTSTANDING ITEMS'));
  if (reworkItemTypes.length > 0) {
    lines.push('Requested by intake team:');
    for (const itemType of reworkItemTypes) {
      const label = REWORK_ITEM_LABELS[itemType] || itemType;
      lines.push(`  •  ${label}`);
    }
  } else if (missingFields.length > 0 && !wasOverridden) {
    lines.push('Engine-flagged missing items:');
    for (const f of missingFields) {
      lines.push(`  •  ${f.label}`);
    }
  } else {
    lines.push('  No outstanding missing item requests.');
  }
  lines.push('');

  // ── Attachments ─────────────────────────────────────────────────────────────
  lines.push(section('ATTACHMENTS'));
  if (attachmentFiles.length > 0) {
    for (const att of attachmentFiles) {
      const typeLabel = ATT_TYPE_LABELS[att.type] || att.type || 'Other';
      const statusLabel = ehrAttStatus(att.status);
      lines.push(`  ${typeLabel.padEnd(20)} ${statusLabel}`);
    }
  } else {
    lines.push('  No attachments submitted.');
  }
  lines.push('');

  // ── Workflow / Next Step ─────────────────────────────────────────────────────
  lines.push(section('WORKFLOW / NEXT STEP'));
  if (workflowStatus) {
    const nextStep = NEXT_STEP_LABELS[workflowStatus]
      || `Workflow status: ${workflowStatus}`;
    lines.push(`  ${nextStep}`);
  } else {
    lines.push('  Awaiting triage review by intake team');
  }
  lines.push('');

  // ── Footer ──────────────────────────────────────────────────────────────────
  lines.push(line('─'));
  lines.push(`Generated by ReferralGate (NO PHI).`);
  lines.push(`Receipt Token: ${receiptToken || '—'}`);
  lines.push(line('─'));

  return lines.join('\n');
};
